import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  downloadReconciliationTemplate,
  uploadBankStatement,
  listBankStatements,
  getBankStatement,
  deleteBankStatement,
  confirmBankEntry,
  bulkConfirmBankEntries,
  ignoreBankEntry,
  unconfirmBankEntry,
} from '../api';
import { formatDateDdMmmYy, formatRupeesUI } from '../utils/format';
import { showToast } from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────

type ReceivableSource = 'invoice' | 'opening';

interface Suggestion {
  source: ReceivableSource;
  invoiceId: number;            // target id (invoice or opening balance, per source)
  invoiceNumber: string;
  outstanding: number;
  clientName: string;
  score: number;
  reasons: string[];
  tdsHint: { rate: number; tdsAmount: number } | null;
  isPartial?: boolean;
}

interface OutstandingInvoice {
  source: ReceivableSource;
  id: number;
  invoiceNumber: string;
  invoiceDate: string;
  clientId: number;
  clientName: string;
  clientCode: string;
  totalAmount: number;
  outstanding: number;
}

interface EntryPayment {
  id: string;                   // "inv-<id>" | "ob-<id>"
  source: ReceivableSource;
  amount: number;
  tdsDeducted: number | null;
  docNumber: string;
  clientName: string;
}

interface Entry {
  id: number;
  rowNumber: number;
  txnDate: string;
  narration: string;
  reference: string | null;
  debit: number | null;
  credit: number | null;
  balance: number | null;
  status: 'UNMATCHED' | 'CONFIRMED' | 'IGNORED';
  ignoredReason: string | null;
  payments: EntryPayment[];
  suggestions: Suggestion[];
}

interface StatementSummary {
  id: number;
  fileName: string;
  bankAccount: string | null;
  rowCount: number;
  creditRows: number;
  matchedCount: number;
  uploadedAt: string;
  uploadedBy: { email: string; staff?: { staffName: string } | null } | null;
  progress: { confirmed: number; ignored: number; unmatched: number };
}

interface Statement extends StatementSummary {
  entries: Entry[];
  outstandingInvoices: OutstandingInvoice[];
}

// ─── Page ─────────────────────────────────────────────────────────────────

const Reconciliation: React.FC = () => {
  const { isAdmin } = useAuth();
  const [statements, setStatements] = useState<StatementSummary[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [active, setActive] = useState<Statement | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingActive, setLoadingActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bankAccount, setBankAccount] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // Phase-2: bulk selection across UNMATCHED entries with at least one suggestion.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  // ── Initial list ─────────────────────────────────────────────────────────
  const refreshList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await listBankStatements();
      setStatements(res.data);
      if (res.data.length > 0 && activeId == null) setActiveId(res.data[0].id);
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Failed to load statements', 'error');
    } finally {
      setLoadingList(false);
    }
  }, [activeId]);

  // Run once on mount; refreshList captures the live activeId via closure
  // refresh and we intentionally don't want it to re-run when activeId changes.
  // eslint-disable-next-line
  useEffect(() => { refreshList(); }, []);

  // ── Load active statement ────────────────────────────────────────────────
  const refreshActive = useCallback(async () => {
    if (activeId == null) { setActive(null); return; }
    setLoadingActive(true);
    try {
      const res = await getBankStatement(activeId);
      setActive(res.data);
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Failed to load statement', 'error');
    } finally {
      setLoadingActive(false);
    }
  }, [activeId]);
  useEffect(() => { refreshActive(); }, [refreshActive]);

  // ── Template + upload ────────────────────────────────────────────────────
  const handleTemplate = async () => {
    try {
      const res = await downloadReconciliationTemplate();
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Bank_Statement_Template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Failed to download template', 'error');
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const res = await uploadBankStatement(file, bankAccount || undefined);
      const skipped = res.data.skippedDuplicates ?? 0;
      const imported = res.data.importedRows ?? 0;
      showToast(
        `Imported ${imported} rows${skipped > 0 ? ` · skipped ${skipped} duplicates` : ''}`,
        'success',
      );
      setBankAccount('');
      if (fileRef.current) fileRef.current.value = '';
      await refreshList();
      setActiveId(res.data.id);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Upload failed';
      const warnings: string[] = err?.response?.data?.warnings ?? [];
      showToast(warnings.length ? `${msg} — ${warnings[0]}` : msg, 'error');
    } finally {
      setUploading(false);
    }
  };

  // ── Delete statement (admin only) ─────────────────────────────────────────
  const handleDeleteStatement = async (s: StatementSummary) => {
    if (!window.confirm(
      `Delete statement "${s.fileName}" and all ${s.creditRows} imported rows?\n\n` +
      `This cannot be undone. Any confirmed matches (receipts) must be unconfirmed first.`,
    )) return;
    try {
      await deleteBankStatement(s.id);
      showToast('Statement deleted', 'success');
      if (activeId === s.id) { setActiveId(null); setActive(null); }
      await refreshList();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Failed to delete statement', 'error');
    }
  };

  // ── Entry actions ────────────────────────────────────────────────────────
  const onConfirm = async (entryId: number, source: ReceivableSource, targetId: number, tdsAmount?: number) => {
    try {
      const target = source === 'opening' ? { openingBalanceId: targetId } : { invoiceId: targetId };
      await confirmBankEntry(entryId, { ...target, tdsAmount });
      showToast('Matched & payment recorded', 'success');
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(entryId); return n; });
      await refreshActive(); await refreshList();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Confirm failed', 'error');
    }
  };

  const onConfirmSplit = async (
    entryId: number,
    allocations: Array<{ source: ReceivableSource; targetId: number; amount: number; tdsAmount?: number }>,
  ) => {
    try {
      const payload = allocations.map((a) => ({
        ...(a.source === 'opening' ? { openingBalanceId: a.targetId } : { invoiceId: a.targetId }),
        amount: a.amount, tdsAmount: a.tdsAmount,
      }));
      await confirmBankEntry(entryId, { allocations: payload });
      showToast(`Split across ${allocations.length} receivables`, 'success');
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(entryId); return n; });
      await refreshActive(); await refreshList();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Split confirm failed', 'error');
    }
  };

  const onBulkConfirm = async (minScore: number) => {
    if (selectedIds.size === 0) return;
    setBulkRunning(true);
    try {
      const res = await bulkConfirmBankEntries(Array.from(selectedIds), minScore);
      const { confirmed, skipped } = res.data;
      showToast(
        `Bulk: ${confirmed} confirmed${skipped > 0 ? ` · ${skipped} skipped (no high-confidence match)` : ''}`,
        confirmed > 0 ? 'success' : 'error',
      );
      setSelectedIds(new Set());
      await refreshActive(); await refreshList();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Bulk confirm failed', 'error');
    } finally {
      setBulkRunning(false);
    }
  };

  // Selection helpers
  const toggleSelect = (id: number) =>
    setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectableEntries = useMemo(
    () => (active?.entries ?? []).filter(
      (e) => e.status === 'UNMATCHED' && (e.credit ?? 0) > 0 && e.suggestions.length > 0,
    ),
    [active],
  );
  const allSelected = selectableEntries.length > 0 && selectableEntries.every((e) => selectedIds.has(e.id));
  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(selectableEntries.map((e) => e.id)));
  };

  const onIgnore = async (entryId: number) => {
    const reason = window.prompt('Reason for ignoring this row? (e.g. Bank charges, Internal transfer)') ?? '';
    try {
      await ignoreBankEntry(entryId, reason || undefined);
      showToast('Row ignored', 'success');
      await refreshActive(); await refreshList();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Ignore failed', 'error');
    }
  };

  const onUnconfirm = async (entryId: number) => {
    if (!window.confirm('Reverse this match? The associated payment will be deleted and the invoice status updated.')) return;
    try {
      await unconfirmBankEntry(entryId);
      showToast('Match reversed', 'success');
      await refreshActive(); await refreshList();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Unconfirm failed', 'error');
    }
  };

  // ── Counts for the active statement ──────────────────────────────────────
  const counts = useMemo(() => {
    if (!active) return { unmatched: 0, confirmed: 0, ignored: 0, debit: 0 };
    const c = { unmatched: 0, confirmed: 0, ignored: 0, debit: 0 };
    for (const e of active.entries) {
      if ((e.debit ?? 0) > 0) c.debit++;
      else if (e.status === 'UNMATCHED') c.unmatched++;
      else if (e.status === 'CONFIRMED') c.confirmed++;
      else if (e.status === 'IGNORED')   c.ignored++;
    }
    return c;
  }, [active]);

  return (
    <div className="max-w-7xl">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">Bank Reconciliation</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Upload a bank statement in the canonical format, then confirm each suggested invoice match to record a payment.
        </p>
      </div>

      {/* Upload card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-gray-500 font-medium block mb-1">Bank account label (optional)</label>
            <input
              type="text"
              value={bankAccount}
              onChange={(e) => setBankAccount(e.target.value)}
              placeholder="e.g. Kotak — 4013021519"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium block mb-1">Statement file (.xlsx, .xls, .csv)</label>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              disabled={uploading}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); }}
              className="text-sm"
            />
          </div>
          <button onClick={handleTemplate} className="text-sm text-blue-600 hover:text-blue-800 underline">
            Download template
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Required columns: <span className="font-mono">Date · Description · Debit · Credit</span>. Optional but recommended: <span className="font-mono">Reference · Balance</span>.
          Re-uploading the same file is safe — duplicate rows are detected and skipped.
        </p>
      </div>

      <div className="space-y-3">
        {/* Statement selector — a compact strip above the table (was a 280px
            sidebar that stole width from the entries) so the full width is
            available for entry details. */}
        {loadingList ? (
          <div className="text-sm text-gray-400">Loading statements…</div>
        ) : statements.length === 0 ? (
          <div className="text-sm text-gray-400">No statements yet — upload one above.</div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Statements:</span>
            {statements.map((s) => {
              const active = s.id === activeId;
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveId(s.id)}
                  title={`${s.bankAccount || 'No bank label'} · uploaded ${formatDateDdMmmYy(s.uploadedAt)}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                    active ? 'bg-blue-50 border-blue-300 text-blue-900' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="font-medium truncate max-w-[180px]">{s.fileName}</span>
                  <span className="flex items-center gap-1.5 text-[11px]">
                    <span className="text-emerald-700">✓{s.progress.confirmed}</span>
                    <span className="text-amber-600">○{s.progress.unmatched}</span>
                    {s.progress.ignored > 0 && <span className="text-gray-400">–{s.progress.ignored}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Main: entries — now full width */}
        <main className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden min-w-0">
          {!active ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              {loadingActive ? 'Loading…' : 'Select a statement to view entries.'}
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{active.fileName}</div>
                  <div className="text-xs text-gray-500">
                    {active.bankAccount || 'No bank label'} · uploaded {formatDateDdMmmYy(active.uploadedAt)}
                    {active.uploadedBy?.staff?.staffName ? ` by ${active.uploadedBy.staff.staffName}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                    ✓ {counts.confirmed} matched
                  </span>
                  <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                    ○ {counts.unmatched} pending
                  </span>
                  {counts.ignored > 0 && (
                    <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                      – {counts.ignored} ignored
                    </span>
                  )}
                  {counts.debit > 0 && (
                    <span className="px-2 py-0.5 rounded bg-gray-50 text-gray-500 border border-gray-200">
                      {counts.debit} debit rows
                    </span>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => handleDeleteStatement(active)}
                      className="ml-1 pl-3 border-l border-gray-300 text-red-600 hover:text-red-800 font-medium"
                      title={counts.confirmed > 0
                        ? 'Unconfirm the matched rows first, then this statement can be deleted'
                        : 'Delete this statement and all its imported rows (admin only)'}
                    >
                      🗑 Delete
                    </button>
                  )}
                </div>
              </div>

              {/* Bulk action bar — sticky when any rows are selected */}
              {selectedIds.size > 0 && (
                <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center justify-between gap-3 flex-wrap text-sm">
                  <span className="text-blue-800">
                    <strong>{selectedIds.size}</strong> row{selectedIds.size === 1 ? '' : 's'} selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => onBulkConfirm(100)}
                      disabled={bulkRunning}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-3 py-1 rounded text-xs font-medium"
                      title="Auto-confirm only rows whose top suggestion is an exact amount match"
                    >
                      {bulkRunning ? 'Working…' : 'Confirm exact matches'}
                    </button>
                    <button
                      onClick={() => onBulkConfirm(70)}
                      disabled={bulkRunning}
                      className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-3 py-1 rounded text-xs font-medium"
                      title="Also include rows whose top suggestion matches outstanding net of TDS"
                    >
                      Confirm exact + TDS-net matches
                    </button>
                    <button
                      onClick={() => setSelectedIds(new Set())}
                      className="text-blue-700 hover:text-blue-900 px-2 py-1 text-xs"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 w-8">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          disabled={selectableEntries.length === 0}
                          title="Select all unmatched rows that have at least one suggestion"
                        />
                      </th>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2 max-w-[300px]">Narration</th>
                      <th className="text-left px-3 py-2">Reference</th>
                      <th className="text-right px-3 py-2">Credit</th>
                      <th className="text-left px-3 py-2">Suggested match / action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Only credit rows are matched to invoices — debit rows
                        (money out) are hidden entirely, not just the column. */}
                    {active.entries.filter((e) => (e.credit ?? 0) > 0).map((e) => <EntryRow key={e.id} entry={e}
                      outstandingInvoices={active.outstandingInvoices}
                      selectable={e.status === 'UNMATCHED' && e.suggestions.length > 0}
                      selected={selectedIds.has(e.id)}
                      onToggleSelect={() => toggleSelect(e.id)}
                      onConfirm={onConfirm} onConfirmSplit={onConfirmSplit}
                      onIgnore={onIgnore} onUnconfirm={onUnconfirm}
                    />)}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

// ─── Entry row ─────────────────────────────────────────────────────────────

const EntryRow: React.FC<{
  entry: Entry;
  outstandingInvoices: OutstandingInvoice[];
  selectable: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onConfirm: (entryId: number, source: ReceivableSource, targetId: number, tdsAmount?: number) => void;
  onConfirmSplit: (entryId: number, allocations: Array<{ source: ReceivableSource; targetId: number; amount: number; tdsAmount?: number }>) => void;
  onIgnore: (entryId: number) => void;
  onUnconfirm: (entryId: number) => void;
}> = ({ entry, outstandingInvoices, selectable, selected, onToggleSelect, onConfirm, onConfirmSplit, onIgnore, onUnconfirm }) => {
  const isCredit = (entry.credit ?? 0) > 0;
  const [splitOpen, setSplitOpen] = useState(false);

  // Colour the whole row by match state so confirmed/ignored rows are easy to
  // scan past and the pending (unmatched) ones stand out.
  const rowClass =
    entry.status === 'CONFIRMED' ? 'bg-emerald-50/70 hover:bg-emerald-50'
    : entry.status === 'IGNORED' ? 'bg-gray-50 text-gray-500 hover:bg-gray-100'
    : 'hover:bg-amber-50/40';

  return (
    <tr className={`border-b border-gray-100 align-top ${rowClass}`}>
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          disabled={!selectable}
          title={selectable ? 'Select for bulk confirm' : 'Not selectable'}
        />
      </td>
      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatDateDdMmmYy(entry.txnDate)}</td>
      <td className="px-3 py-2 text-gray-700 max-w-[300px]" title={entry.narration}>
        <div className="truncate">{entry.narration}</div>
      </td>
      <td className="px-3 py-2 text-gray-500 whitespace-nowrap text-xs font-mono">{entry.reference || '—'}</td>
      <td className="px-3 py-2 text-right whitespace-nowrap font-medium text-emerald-700">
        {isCredit ? `₹${formatRupeesUI(entry.credit!)}` : ''}
      </td>
      <td className="px-3 py-2">
        {entry.status === 'IGNORED' ? (
          <div className="text-xs text-gray-500">
            <span className="px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 mr-1">Ignored</span>
            {entry.ignoredReason && <span className="italic">{entry.ignoredReason}</span>}
            <button onClick={() => onUnconfirm(entry.id)} className="ml-2 text-blue-600 hover:underline">undo</button>
          </div>
        ) : entry.status === 'CONFIRMED' ? (
          <div className="text-xs">
            {entry.payments.map((p) => (
              <div key={p.id} className="flex items-center gap-2 mb-0.5">
                <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">
                  {p.docNumber}
                </span>
                {p.source === 'opening' && (
                  <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-medium">OB</span>
                )}
                <span className="text-gray-700">{p.clientName}</span>
                <span className="text-gray-500">₹{formatRupeesUI(p.amount)}</span>
                {p.tdsDeducted ? <span className="text-orange-600">+ TDS ₹{formatRupeesUI(p.tdsDeducted)}</span> : null}
              </div>
            ))}
            <button onClick={() => onUnconfirm(entry.id)} className="text-blue-600 hover:underline">undo match</button>
          </div>
        ) : (
          <div className="space-y-1.5">
            {entry.suggestions.length === 0 && !splitOpen && (
              <div className="text-xs text-gray-400 italic">No automatic match found</div>
            )}
            {entry.suggestions.map((s) => {
              const credit = Number(entry.credit ?? 0);
              const remainderAfter = s.outstanding - credit - (s.tdsHint?.tdsAmount ?? 0);
              const partialHint = !s.tdsHint && credit < s.outstanding - 1
                ? `₹${formatRupeesUI(Math.max(0, remainderAfter))} will remain outstanding after this payment`
                : null;
              return (
                <div key={`${s.source}-${s.invoiceId}`} className="flex items-center gap-2 text-xs flex-wrap">
                  <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-mono">
                    {s.invoiceNumber}
                  </span>
                  {s.source === 'opening' && (
                    <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-medium" title="Opening balance (imported legacy receivable)">OB</span>
                  )}
                  <span className="text-gray-700 truncate max-w-[180px]" title={s.clientName}>{s.clientName}</span>
                  <span className="text-gray-500">outstanding ₹{formatRupeesUI(s.outstanding)}</span>
                  {s.tdsHint && (
                    <span className="px-1 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200" title={s.reasons.join(' · ')}>
                      TDS {(s.tdsHint.rate * 100).toFixed(s.tdsHint.rate < 0.1 ? 1 : 0)}%
                    </span>
                  )}
                  {s.isPartial && (
                    <span className="px-1 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" title={s.reasons.join(' · ')}>
                      partial
                    </span>
                  )}
                  {partialHint && <span className="text-amber-600 text-[11px]">{partialHint}</span>}
                  <button
                    onClick={() => onConfirm(entry.id, s.source, s.invoiceId, s.tdsHint?.tdsAmount)}
                    className="ml-auto bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded text-xs font-medium"
                    title={s.reasons.join(' · ')}
                  >
                    Confirm
                  </button>
                </div>
              );
            })}
            <div className="flex items-center gap-2 pt-0.5">
              <button
                onClick={() => setSplitOpen((v) => !v)}
                className={`px-2 py-0.5 rounded border text-xs font-medium ${
                  splitOpen
                    ? 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    : entry.suggestions.length === 0
                      ? 'border-blue-400 text-blue-700 bg-blue-50 hover:bg-blue-100'
                      : 'border-blue-300 text-blue-700 hover:bg-blue-50'
                }`}
                title="Pick an invoice yourself, or split this credit across several invoices"
              >
                {splitOpen ? 'Hide' : '🔗 Match manually / split'}
              </button>
              <button
                onClick={() => onIgnore(entry.id)}
                className="px-2 py-0.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 text-xs"
                title="Not a client receipt (bank charges, internal transfer, etc.) — hide it from pending"
              >
                Ignore
              </button>
            </div>
            {splitOpen && isCredit && (
              <SplitAllocationPanel
                creditAmount={Number(entry.credit ?? 0)}
                outstandingInvoices={outstandingInvoices}
                seedSuggestions={entry.suggestions}
                onCancel={() => setSplitOpen(false)}
                onSubmit={(allocs) => { setSplitOpen(false); onConfirmSplit(entry.id, allocs); }}
              />
            )}
          </div>
        )}
      </td>
    </tr>
  );
};

// ─── Searchable invoice picker ────────────────────────────────────────────
// Type-to-search combobox over outstanding invoices — replaces a plain
// <select> that was unusable once there are more than a handful of invoices.
const InvoiceCombobox: React.FC<{
  invoices: OutstandingInvoice[];
  value?: OutstandingInvoice;
  onChange: (item: OutstandingInvoice) => void;
}> = ({ invoices, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const selected = value;
  const sameItem = (a: OutstandingInvoice | undefined, b: OutstandingInvoice) => !!a && a.source === b.source && a.id === b.id;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const CAP = 50;
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter(
      (i) =>
        i.invoiceNumber.toLowerCase().includes(q) ||
        i.clientName.toLowerCase().includes(q) ||
        i.clientCode.toLowerCase().includes(q),
    );
  }, [invoices, query]);
  const shown = matches.slice(0, CAP);

  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-1.5 py-1 rounded border border-gray-300 bg-white truncate"
        title={selected ? `${selected.invoiceNumber} — ${selected.clientName}` : 'Pick an invoice'}
      >
        {selected
          ? `${selected.invoiceNumber}${selected.source === 'opening' ? ' (OB)' : ''} — ${selected.clientName.slice(0, 22)}${selected.clientName.length > 22 ? '…' : ''} (₹${formatRupeesUI(selected.outstanding)})`
          : <span className="text-gray-400">Pick a receivable…</span>}
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-[440px] max-w-[85vw] bg-white border border-gray-200 rounded-lg shadow-lg">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search invoice/OB #, client name or code…"
            className="w-full px-2 py-1.5 border-b border-gray-200 text-xs rounded-t-lg focus:outline-none"
          />
          <div className="max-h-60 overflow-y-auto">
            {shown.length === 0 ? (
              <div className="px-2 py-3 text-xs text-gray-400 text-center">No matching receivables</div>
            ) : shown.map((i) => (
              <button
                key={`${i.source}-${i.id}`}
                type="button"
                onClick={() => { onChange(i); setOpen(false); setQuery(''); }}
                className={`w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 flex items-center justify-between gap-2 ${sameItem(value, i) ? 'bg-blue-50' : ''}`}
              >
                <span className="truncate">
                  <span className="font-mono text-blue-700">{i.invoiceNumber}</span>
                  {i.source === 'opening' && <span className="ml-1 px-1 rounded bg-amber-100 text-amber-700 text-[10px]">OB</span>}
                  <span className="text-gray-600"> — {i.clientName}</span>
                </span>
                <span className="text-gray-500 whitespace-nowrap">₹{formatRupeesUI(i.outstanding)}</span>
              </button>
            ))}
            {matches.length > CAP && (
              <div className="px-2 py-1.5 text-[11px] text-gray-400 text-center border-t border-gray-100">
                Showing first {CAP} of {matches.length} — type to narrow
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Split allocation panel ───────────────────────────────────────────────

interface AllocRow {
  source: ReceivableSource;
  targetId: number;     // 0 = nothing picked yet
  amount: string;       // controlled input — keep as string so we can show empties
  tdsAmount: string;
}

const SplitAllocationPanel: React.FC<{
  creditAmount: number;
  outstandingInvoices: OutstandingInvoice[];
  seedSuggestions: Suggestion[];
  onCancel: () => void;
  onSubmit: (allocations: Array<{ source: ReceivableSource; targetId: number; amount: number; tdsAmount?: number }>) => void;
}> = ({ creditAmount, outstandingInvoices, seedSuggestions, onCancel, onSubmit }) => {
  const first = outstandingInvoices[0];
  const [rows, setRows] = useState<AllocRow[]>(() => {
    // Seed from the top suggestion (carrying its source); else the first
    // receivable in the list. TDS hint prefills the tds box.
    if (seedSuggestions.length === 0) {
      return [{ source: first?.source ?? 'invoice', targetId: first?.id ?? 0, amount: String(creditAmount), tdsAmount: '' }];
    }
    const s = seedSuggestions[0];
    return [{ source: s.source, targetId: s.invoiceId, amount: String(creditAmount), tdsAmount: s.tdsHint?.tdsAmount?.toFixed(2) ?? '' }];
  });

  const setRow = (idx: number, patch: Partial<AllocRow>) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () =>
    setRows((prev) => [...prev, { source: first?.source ?? 'invoice', targetId: first?.id ?? 0, amount: '0', tdsAmount: '' }]);
  const removeRow = (idx: number) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const remaining = creditAmount - total;
  const canSubmit = rows.length > 0 && rows.every((r) => r.targetId > 0 && Number(r.amount) > 0) && remaining >= -0.01;

  const handleSubmit = () => {
    const allocs = rows.map((r) => ({
      source: r.source,
      targetId: r.targetId,
      amount: Number(r.amount),
      tdsAmount: r.tdsAmount ? Number(r.tdsAmount) : undefined,
    }));
    onSubmit(allocs);
  };

  return (
    <div className="mt-2 p-3 bg-blue-50/40 border border-blue-200 rounded-lg">
      <div className="text-xs font-semibold text-blue-900 mb-2">
        Split allocation — credit ₹{formatRupeesUI(creditAmount)}
      </div>
      <div className="space-y-1.5">
        {rows.map((r, idx) => {
          const inv = outstandingInvoices.find((i) => i.source === r.source && i.id === r.targetId);
          return (
            <div key={idx} className="flex items-center gap-1.5 text-xs">
              <InvoiceCombobox
                invoices={outstandingInvoices}
                value={inv}
                onChange={(item) => setRow(idx, { source: item.source, targetId: item.id })}
              />
              <span className="text-gray-500">₹</span>
              <input
                type="number"
                value={r.amount}
                onChange={(e) => setRow(idx, { amount: e.target.value })}
                placeholder="amount"
                className="w-24 px-1.5 py-1 rounded border border-gray-300 text-right"
                step="0.01"
              />
              <span className="text-orange-600 text-[11px]">TDS</span>
              <input
                type="number"
                value={r.tdsAmount}
                onChange={(e) => setRow(idx, { tdsAmount: e.target.value })}
                placeholder="0"
                className="w-20 px-1.5 py-1 rounded border border-gray-300 text-right text-orange-700"
                step="0.01"
              />
              {inv && (Number(r.amount) + (Number(r.tdsAmount) || 0)) > inv.outstanding + 1 && (
                <span className="text-red-600 text-[11px]" title="Exceeds this receivable's outstanding">!</span>
              )}
              <button onClick={() => removeRow(idx)} className="text-gray-400 hover:text-red-600 px-1" title="Remove row">×</button>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-blue-200 text-xs">
        <button onClick={addRow} className="text-blue-700 hover:underline">+ add another receivable</button>
        <div className="flex items-center gap-3">
          <span>Total allocated: <strong>₹{formatRupeesUI(total)}</strong></span>
          <span className={remaining > 0.01 ? 'text-amber-700' : remaining < -0.01 ? 'text-red-600' : 'text-emerald-700'}>
            {remaining > 0.01 ? `₹${formatRupeesUI(remaining)} unallocated`
             : remaining < -0.01 ? `Over by ₹${formatRupeesUI(-remaining)}`
             : '✓ Fully allocated'}
          </span>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <button onClick={onCancel} className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1">Cancel</button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-3 py-1 rounded text-xs font-medium"
        >
          Confirm {rows.length} allocation{rows.length === 1 ? '' : 's'}
        </button>
      </div>
    </div>
  );
};

export default Reconciliation;
